import ResponsiveWrapper from "../ResponsiveWrapper/ResponsiveWrapper";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar, Line } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { getCategoryColor } from "../../utils/categoryColors";
import { formatCurrency } from "../../utils/format";
import { getLocalizedMonthNameFromYearMonth } from "../../utils/date";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { useScreenHeight, getChartHeight } from "../../hooks/useScreenHeight";

const margin = { top: 20, right: 85, bottom: 50, left: 40 };

const BarChart = ({ data, averages, translate, chartType = "expenses" }) => {
	const { locale } = useUserPreferences();
	const screenHeight = useScreenHeight();
	const chartHeight = getChartHeight(screenHeight);

	// Tooltip setup
	const {
		tooltipData,
		tooltipLeft,
		tooltipTop,
		tooltipOpen,
		showTooltip,
		hideTooltip,
	} = useTooltip();
	const { containerRef, TooltipInPortal } = useTooltipInPortal({
		scroll: true,
	});

	const renderChart = ({ width, height }) => {
		if (width === 0 || height === 0) return null;

		const xMax = width - margin.left - margin.right;
		const yMax = height - margin.top - margin.bottom;

		const xScale = scaleBand({
			domain: data.map(d => d.month),
			range: [0, xMax],
			padding: 0.1,
		});

		// Map of month ids ("2025-01") to localized month names ("janvier", "February", etc.)
		const monthLabels = {};
		data.forEach(d => {
			if (!monthLabels[d.month]) {
				monthLabels[d.month] = getLocalizedMonthNameFromYearMonth(
					d.month,
					locale
				);
			}
		});

		// Calculate values for the Y scale based on the chart type
		let allValues = [];
		let avgValues = [];

		if (chartType === "overview") {
			// For the overview: income, expenses, balance
			allValues = data.flatMap(d => [
				d.income || 0,
				d.expenses || 0,
				d.balance || 0,
			]);
			avgValues = averages
				? [averages.income || 0, averages.expenses || 0, averages.balance || 0]
				: [];
		} else {
			// For expenses by category
			allValues = data.flatMap(d =>
				typeof d.value === "number"
					? [d.value]
					: Object.values(d.categories || {})
			);
			avgValues = averages ? Object.values(averages) : [];
		}

		const maxValue = Math.max(...allValues, ...avgValues, 0);
		const minValue = Math.min(...allValues, ...avgValues, 0);

		const yScale = scaleLinear({
			domain: [minValue < 0 ? minValue * 1.1 : 0, maxValue * 1.1],
			nice: true,
			range: [yMax, 0],
		});

		// Rendering according to the chart type
		if (chartType === "overview") {
			// Overview mode: 3 bars per month (income, expenses, balance)
			const incomeColor = getCategoryColor("income");
			const expenseColor = getCategoryColor("general");
			const balancePositiveColor = "#10B981";
			const balanceNegativeColor = "#EF4444";

			const groupWidth = xScale.bandwidth();
			const barWidth = groupWidth / 3;

			return (
				<svg width={width} height={height}>
					<Group top={margin.top} left={margin.left}>
						{/* Zero line */}
						<Line
							from={{ x: 0, y: yScale(0) }}
							to={{ x: xMax, y: yScale(0) }}
							stroke="var(--text-color)"
							strokeWidth={1}
							opacity={0.3}
						/>

						{/* Bars per month */}
						{data.map((monthData, monthIndex) => {
							const monthX = xScale(monthData.month);
							if (monthX == null) return null;

							const monthLabel = monthLabels[monthData.month];

							// Income bar
							const incomeBar =
								monthData.income > 0 ? (
									<Bar
										key={`income-${monthData.month}`}
										className="chart-bar-animated"
										x={monthX}
										y={yScale(monthData.income)}
										width={barWidth * 0.9}
										height={yScale(0) - yScale(monthData.income)}
										fill={incomeColor}
										rx={4}
										onMouseMove={e => {
											const event = e.nativeEvent;
											showTooltip({
												tooltipLeft: event.offsetX,
												tooltipTop: event.offsetY,
												tooltipData: {
													category: "income",
													label: monthLabel,
													value: monthData.income,
												},
											});
										}}
										onMouseLeave={hideTooltip}
									/>
								) : null;

							// Expenses bar
							const expensesBar =
								monthData.expenses > 0 ? (
									<Bar
										key={`expenses-${monthData.month}`}
										className="chart-bar-animated"
										x={monthX + barWidth}
										y={yScale(monthData.expenses)}
										width={barWidth * 0.9}
										height={yScale(0) - yScale(monthData.expenses)}
										fill={expenseColor}
										rx={4}
										onMouseMove={e => {
											const event = e.nativeEvent;
											showTooltip({
												tooltipLeft: event.offsetX,
												tooltipTop: event.offsetY,
												tooltipData: {
													category: "expenses",
													label: monthLabel,
													value: monthData.expenses,
												},
											});
										}}
										onMouseLeave={hideTooltip}
									/>
								) : null;

							// Balance bar
							const balance = monthData.balance || 0;
							const balanceBar =
								balance !== 0 ? (
									<Bar
										key={`balance-${monthData.month}`}
										className="chart-bar-animated"
										x={monthX + barWidth * 2}
										y={balance > 0 ? yScale(balance) : yScale(0)}
										width={barWidth * 0.9}
										height={Math.abs(yScale(balance) - yScale(0))}
										fill={
											balance > 0 ? balancePositiveColor : balanceNegativeColor
										}
										rx={4}
										onMouseMove={e => {
											const event = e.nativeEvent;
											showTooltip({
												tooltipLeft: event.offsetX,
												tooltipTop: event.offsetY,
												tooltipData: {
													category: "balance",
													label: monthLabel,
													value: balance,
												},
											});
										}}
										onMouseLeave={hideTooltip}
									/>
								) : null;

							return (
								<Group key={`month-${monthData.month}`}>
									{incomeBar}
									{expensesBar}
									{balanceBar}
								</Group>
							);
						})}

						{/* Average lines */}
						{averages && averages.income > 0 && (
							<Group>
								<Line
									from={{ x: 0, y: yScale(averages.income) }}
									to={{ x: xMax, y: yScale(averages.income) }}
									stroke={incomeColor}
									strokeWidth={2}
									strokeDasharray="5,5"
									opacity={0.7}
								/>
								<text
									x={xMax + 5}
									y={yScale(averages.income)}
									dy="0.3em"
									fontSize={10}
									fill={incomeColor}
									fontWeight="bold"
								>
									{translate("analysis.average")}:{" "}
									{formatCurrency(averages.income, locale)}
								</text>
							</Group>
						)}

						{averages && averages.expenses > 0 && (
							<Group>
								<Line
									from={{ x: 0, y: yScale(averages.expenses) }}
									to={{ x: xMax, y: yScale(averages.expenses) }}
									stroke={expenseColor}
									strokeWidth={2}
									strokeDasharray="5,5"
									opacity={0.7}
								/>
								<text
									x={xMax + 5}
									y={yScale(averages.expenses)}
									dy="0.3em"
									fontSize={10}
									fill={expenseColor}
									fontWeight="bold"
								>
									{translate("analysis.average")}:{" "}
									{formatCurrency(averages.expenses, locale)}
								</text>
							</Group>
						)}

						{averages && averages.balance !== 0 && (
							<Group>
								<Line
									from={{ x: 0, y: yScale(averages.balance) }}
									to={{ x: xMax, y: yScale(averages.balance) }}
									stroke={
										averages.balance > 0
											? balancePositiveColor
											: balanceNegativeColor
									}
									strokeWidth={2}
									strokeDasharray="5,5"
									opacity={0.7}
								/>
								<text
									x={xMax + 5}
									y={yScale(averages.balance)}
									dy="0.3em"
									fontSize={10}
									fill={
										averages.balance > 0
											? balancePositiveColor
											: balanceNegativeColor
									}
									fontWeight="bold"
								>
									{translate("analysis.average")}:{" "}
									{formatCurrency(averages.balance, locale)}
								</text>
							</Group>
						)}

						{/* Y-axis */}
						<AxisLeft
							scale={yScale}
							stroke="var(--text-color)"
							tickLabelProps={() => ({
								fill: "var(--text-color)",
								fontSize: 11,
								textAnchor: "end",
							})}
							numTicks={6}
							tickLineProps={{
								stroke: "var(--text-color)",
								strokeWidth: 1,
							}}
						/>

						{/* X-axis */}
						<AxisBottom
							scale={xScale}
							top={yMax}
							stroke="var(--text-color)"
							tickFormat={d => monthLabels[d] || d}
							tickLabelProps={() => ({
								fill: "var(--text-color)",
								fontSize: 11,
								textAnchor: "middle",
								angle: -45,
								dx: "-0.25em",
								dy: "0.5em",
							})}
							tickLineProps={{
								stroke: "var(--text-color)",
								strokeWidth: 1,
							}}
						/>
					</Group>
				</svg>
			);
		} else {
			// Expense mode: stacked by category
			const categoryKeys = Object.keys(data[0]?.categories || {});
			const barWidth = xScale.bandwidth() / categoryKeys.length;

			return (
				<svg width={width} height={height}>
					<Group top={margin.top} left={margin.left}>
						{/* Bars per category per month */}
						{data.map((monthData, monthIndex) => {
							const monthX = xScale(monthData.month);
							return categoryKeys.map((category, categoryIndex) => {
								const value = monthData.categories[category] || 0;
								if (value === 0) return null;

								const barHeight = yMax - yScale(value);
								const barX = monthX + categoryIndex * barWidth;
								const barY = yScale(value);

								return (
									<Bar
										key={`bar-${monthData.month}-${category}`}
										className="chart-bar-animated"
										x={barX}
										y={barY}
										width={barWidth * 0.8}
										height={barHeight}
										fill={getCategoryColor(category)}
										rx={2}
										onMouseMove={e => {
											const event = e.nativeEvent;
											showTooltip({
												tooltipLeft: event.offsetX,
												tooltipTop: event.offsetY,
												tooltipData: {
													category,
													label: monthLabels[monthData.month],
													value,
												},
											});
										}}
										onMouseLeave={hideTooltip}
									/>
								);
							});
						})}

						{/* Average lines per category */}
						{averages &&
							categoryKeys.map(category => {
								const avgValue = averages[category];
								if (!avgValue || avgValue === 0) return null;

								const y = yScale(avgValue);
								return (
									<Group key={`avg-line-${category}`}>
										<Line
											from={{ x: 0, y }}
											to={{ x: xMax, y }}
											stroke={getCategoryColor(category)}
											strokeWidth={2}
											strokeDasharray="5,5"
											opacity={0.7}
										/>
										<text
											x={xMax + 5}
											y={y}
											dy="0.3em"
											fontSize={10}
											fill={getCategoryColor(category)}
											fontWeight="bold"
										>
											{translate("analysis.average")}:{" "}
											{formatCurrency(avgValue, locale)}
										</text>
									</Group>
								);
							})}

						{/* Y-axis */}
						<AxisLeft
							scale={yScale}
							stroke="var(--text-color)"
							tickLabelProps={() => ({
								fill: "var(--text-color)",
								fontSize: 11,
								textAnchor: "end",
							})}
							numTicks={6}
							tickLineProps={{
								stroke: "var(--text-color)",
								strokeWidth: 1,
							}}
						/>

						{/* X-axis */}
						<AxisBottom
							scale={xScale}
							top={yMax}
							stroke="var(--text-color)"
							tickFormat={d => monthLabels[d] || d}
							tickLabelProps={() => ({
								fill: "var(--text-color)",
								fontSize: 11,
								textAnchor: "middle",
								angle: -45,
								dx: "-0.25em",
								dy: "0.5em",
							})}
							tickLineProps={{
								stroke: "var(--text-color)",
								strokeWidth: 1,
							}}
						/>
					</Group>
				</svg>
			);
		}
	};

	return (
		<div
			className="chart-box"
			style={{ height: `${chartHeight}px` }}
			ref={containerRef}
		>
			<ResponsiveWrapper fallbackHeight={chartHeight}>
				{renderChart}
			</ResponsiveWrapper>
			{tooltipOpen && tooltipData && (
				<TooltipInPortal
					top={tooltipTop}
					left={tooltipLeft}
					className="chart-tooltip"
				>
					<div
						className="tooltip-header"
						style={{
							color:
								tooltipData.category === "balance"
									? tooltipData.value > 0
										? "#10B981" // green
										: "#EF4444" // red
									: tooltipData.category === "expenses"
									? getCategoryColor("general")
									: getCategoryColor(tooltipData.category),
						}}
					>
						{tooltipData.category === "expenses"
							? translate("analysis.expenseTotal")
							: tooltipData.category === "balance"
							? translate("analysis.balance")
							: translate("category." + tooltipData.category)}
					</div>
					<div className="tooltip-label">{tooltipData.label}</div>
					<div className="tooltip-value">
						{formatCurrency(tooltipData.value, locale)}
					</div>
				</TooltipInPortal>
			)}
		</div>
	);
};

export default BarChart;
